import React, { useState } from 'react';

import { FormattedMessage } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button/new';
import { ButtonItem } from '@atlaskit/menu';
import Modal, {
	ModalBody,
	ModalFooter,
	ModalHeader,
	ModalTitle,
	ModalTransition,
} from '@atlaskit/modal-dialog';
import { Text } from '@atlaskit/primitives/compiled';

import { LINKS_MENU_EXTENSION_ANALYTIC_SOURCE } from '../../../../../../../constants';
import type { LinksMenuExtensionActions } from '../../../../../../../controllers/use-links-menu-parameters-and-actions';
import { ITEM, type ItemType, type LinkItemType } from '../../../../../../../types';

import { messages } from './messages';

type DeleteItemButtonProps = {
	extensionActions: LinksMenuExtensionActions;
	item: ItemType;
};
export const DeleteItemButton = ({
	item,
	extensionActions,
}: DeleteItemButtonProps): React.JSX.Element => {
	const [isOpen, setIsOpen] = useState(false);
	const openModal = () => setIsOpen(true);
	const closeModal = () => setIsOpen(false);

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const { deleteItem } = extensionActions;

	const handleDeleteClick = () => {
		if (item.type === ITEM.MENU && item.childItems?.length) {
			openModal();
		} else {
			handleConfirmDelete();
		}
	};

	const handleConfirmDelete = () => {
		createAnalyticsEvent?.({
			type: 'sendTrackEvent',
			data: {
				action: 'deleted',
				actionSubject: 'item',
				source: LINKS_MENU_EXTENSION_ANALYTIC_SOURCE,
				attributes: {
					itemType: (item as LinkItemType).link ? 'link' : 'menu',
				},
			},
		}).fire();

		deleteItem(item.itemId);
	};

	return (
		<>
			<ButtonItem onClick={handleDeleteClick}>
				<FormattedMessage {...messages.invokeDeleteButtonText} />
			</ButtonItem>
			<ModalTransition>
				{isOpen && (
					<Modal onClose={closeModal}>
						<ModalHeader hasCloseButton>
							<ModalTitle appearance="warning">
								<FormattedMessage {...messages.modalHeaderText} />
							</ModalTitle>
						</ModalHeader>
						<ModalBody>
							<Text>
								<FormattedMessage {...messages.modalDescriptionText} />
							</Text>
						</ModalBody>
						<ModalFooter>
							<Button appearance="subtle" onClick={closeModal}>
								<FormattedMessage {...messages.cancelDeleteButtonText} />
							</Button>
							<Button appearance="warning" onClick={handleConfirmDelete}>
								<FormattedMessage {...messages.confirmDeleteButtonText} />
							</Button>
						</ModalFooter>
					</Modal>
				)}
			</ModalTransition>
		</>
	);
};
